import React from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Avatar,
  Button,
} from "@mui/material";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import Colorapps from "../../../configcss/color";
import Navbar from "../../../Components/Navbar";
import Footer from "../../../Components/Footer";

const NST_LOGO = "path/to/nst-logo.png";
const LIHS_LOGO = "path/to/lihs-logo.png";

const disasterReliefFeatures = [
  {
    icon: <VolunteerActivismIcon fontSize="large" />,
    title: "Emergency Aid",
    description:
      "Rapid deployment of essential supplies including food, water, and blankets to affected areas.",
  },
  {
    icon: <LocalShippingIcon fontSize="large" />,
    title: "Logistics Support",
    description:
      "We organize efficient transportation and distribution of relief materials during emergencies.",
  },
  {
    icon: <HealthAndSafetyIcon fontSize="large" />,
    title: "Medical Assistance",
    description:
      "Provision of basic healthcare services and first aid in collaboration with medical teams.",
  },
];

const DisaterRef = () => {
  return (
    <>
      <Navbar />
      <Box sx={{ backgroundColor: Colorapps.textprimary }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            height: { xs: "50vh", md: "70vh" },
            background: `url("https://img.freepik.com/free-photo/volunteers-preparing-humanitarian-aid-indoor_23-2149370380.jpg") center/cover no-repeat`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.8))",
            }}
          />
          <Box
            sx={{
              position: "relative",
              color: Colorapps.textprimary,
              textAlign: "center",
              zIndex: 1,
              px: { xs: 2, md: 4 },
            }}
          >
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{
                fontFamily: "Poppins, sans-serif",
                textShadow: "0px 2px 10px rgba(0,0,0,0.8)",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Together in Times of Crisis
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.3rem" },
                fontFamily: "Roboto, sans-serif",
                textShadow: "0px 2px 5px rgba(0,0,0,0.8)",
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              “He is not a believer whose stomach is filled while the neighbor to his side goes hungry.”
              <br />
              <strong>[Sahih Bukhari]</strong>
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 4,
                backgroundColor: Colorapps.textprimary,
                color: Colorapps.themesecondary,
                fontWeight: "bold",
                padding: "10px 30px",
                borderRadius: "25px",
                "&:hover": {
                  backgroundColor: Colorapps.themeaccent,
                  transform: "scale(1.05)",
                  transition: "all 0.3s ease",
                },
              }}
              href="#disaster-relief"
            >
              Learn More
            </Button>
          </Box>
        </Box>

        {/* Relief Program Section */}
        <Box
          sx={{
            py: 10,
            backgroundColor: Colorapps.backgroundlight,
            boxShadow: `0 4px 20px ${Colorapps.shadowcolor}`,
          }}
          id="disaster-relief"
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                mb: 6,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="img"
                src={NST_LOGO}
                alt=" Relief Logo"
                sx={{ height: { xs: "60px", md: "80px" }, objectFit: "contain" }}
              />
              <Box
                component="img"
                src={LIHS_LOGO}
                alt="LIHS Logo"
                sx={{ height: { xs: "60px", md: "80px" }, objectFit: "contain" }}
              />
            </Box>

            <Box textAlign="center" mb={6}>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  color: Colorapps.textsecondary,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Disaster Relief Program
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  color: Colorapps.textsecondary,
                  fontStyle: "italic",
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                Providing urgent aid, shelter, and hope to communities in distress.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {disasterReliefFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      padding: 4,
                      textAlign: "center",
                      borderRadius: "15px",
                      backgroundColor: Colorapps.textprimary,
                      border: `1px solid ${Colorapps.themeaccent}`,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: `0px 10px 20px ${Colorapps.shadowcolor}`,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: Colorapps.themeaccent,
                        mb: 2,
                        color: Colorapps.textsecondary,
                        padding: 2,
                        width: 60,
                        height: 60,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: "bold",
                        color: Colorapps.textsecondary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        color: Colorapps.textsecondary,
                        fontSize: "1rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Collaboration Section */}
            <Box
              sx={{
                mt: 15,
                p: 4,
                backgroundColor: Colorapps.themeaccent,
                borderRadius: "15px",
                textAlign: "center",
                color: Colorapps.textsecondary,
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ fontFamily: "Poppins, sans-serif" }}
              >
                Partners in Relief
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "1.1rem",
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                In collaboration with Ghani Foundation, Orient Group, and other key partners, we’ve delivered emergency relief to thousands affected by floods, earthquakes, and poverty crises.
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  borderColor: Colorapps.textsecondary,
                  color: Colorapps.textsecondary,
                  fontWeight: "bold",
                  padding: "10px 30px",
                  borderRadius: "25px",
                  "&:hover": {
                    backgroundColor: Colorapps.textsecondary,
                    color: Colorapps.themeaccent,
                    borderColor: Colorapps.textsecondary,
                  },
                }}
              >
                Read More
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default DisaterRef;
